import React from "react";
import { getListQueryStatus, IndexForms, NodeID, QueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	TruncatedText,
	ViewableObserver,
} from "@hex-insights/core";
import { FieldState, FormState } from "@hex-insights/forms";
import { EmptyView } from "../../../../../../Components/Empty";
import { Icon, IconContainer } from "../../../../../../Components/Icon";
import { SearchField } from "../../../../../../Components/Search";
import {
	CourseSectionFilterInput,
	HomeRoomSectionFilterInput,
	mergeFilterInputs,
	SearchFormValues,
	useClassYearCategoryNamesQuery,
	useClassYearCategorySelectQuery,
	useClassYearNamesQuery,
	useClassYearSelectQuery,
	useCourseSectionDetailedSelectListQuery,
	useCourseSectionNamesQuery,
	useHomeRoomSectionNamesQuery,
	useHomeRoomSectionSelectQuery,
	UserGroupFilterInput,
	useSearchFormState,
	useUserGroupDetailedSelectListQuery,
	useUserGroupNamesQuery,
	useUserNamesAndImagesQuery,
	useUserNamesQuery,
} from "../../../../../../Utilities";
import {
	ClassYearCategoryFormat,
	ClassYearFormat,
	CourseSectionFormat,
	HomeRoomSectionFormat,
	UserFormat,
	UserGroupFormat,
} from "../../../../../Format";
import { SharingMenuContext, SharingMenuFormValues } from "../../utils";
import {
	ClassYearCategoryOption,
	ClassYearOption,
	CourseSectionOption,
	HomeRoomSectionOption,
	UserGroupOption,
	UserOption,
} from "../General";
import { ControlledSharedUsersPreviewListModalController } from "../Preview";
import styles from "./styles.module.css";

type MenuView = "main" | "classYearCategory" | "classYear" | "courseSection" | "homeRoomSection" | "userGroup" | "user";

export type AdvancedSharingModalProps = {
	formState: FormState<SharingMenuFormValues>;
	isPublicFieldState: FieldState<SharingMenuFormValues["isPublic"]>;
	includeEmployeesFieldState: FieldState<SharingMenuFormValues["includeEmployees"]>;
	includeGuardiansFieldState: FieldState<SharingMenuFormValues["includeGuardians"]>;
	includeAllUsersOfPersonTypesFieldState: FieldState<SharingMenuFormValues["includeAllUsersOfPersonTypes"]>;
	classYearCategoryIDsFieldState: FieldState<SharingMenuFormValues["classYearCategoryIDs"]>;
	classYearIDsFieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	courseSectionIDsFieldState: FieldState<SharingMenuFormValues["courseSectionIDs"]>;
	homeRoomSectionIDsFieldState: FieldState<SharingMenuFormValues["homeRoomSectionIDs"]>;
	userGroupIDsFieldState: FieldState<SharingMenuFormValues["userGroupIDs"]>;
	userIDsFieldState: FieldState<SharingMenuFormValues["userIDs"]>;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function AdvancedSharingModal({
	formState,
	isPublicFieldState,
	includeEmployeesFieldState,
	includeGuardiansFieldState,
	includeAllUsersOfPersonTypesFieldState,
	classYearCategoryIDsFieldState,
	classYearIDsFieldState,
	courseSectionIDsFieldState,
	homeRoomSectionIDsFieldState,
	userGroupIDsFieldState,
	userIDsFieldState,
	ifRef,
	onClose,
	className,
	style,
}: AdvancedSharingModalProps) {
	const [view, setView] = React.useState<MenuView>("main");

	const goToMain = React.useCallback(() => {
		setView("main");
	}, []);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Conditional>
				<If condition={view === "main"}>
					<MainMenu
						isPublicFieldState={isPublicFieldState}
						includeEmployeesFieldState={includeEmployeesFieldState}
						includeGuardiansFieldState={includeGuardiansFieldState}
						includeAllUsersOfPersonTypesFieldState={includeAllUsersOfPersonTypesFieldState}
						classYearCategoryIDsFieldState={classYearCategoryIDsFieldState}
						classYearIDsFieldState={classYearIDsFieldState}
						courseSectionIDsFieldState={courseSectionIDsFieldState}
						homeRoomSectionIDsFieldState={homeRoomSectionIDsFieldState}
						userGroupIDsFieldState={userGroupIDsFieldState}
						userIDsFieldState={userIDsFieldState}
						setView={setView}
					/>
				</If>
				<If condition={view === "classYearCategory"}>
					<ClassYearCategoryMenu fieldState={classYearCategoryIDsFieldState} onBack={goToMain} />
				</If>
				<If condition={view === "classYear"}>
					<ClassYearMenu fieldState={classYearIDsFieldState} onBack={goToMain} />
				</If>
				<If condition={view === "homeRoomSection"}>
					<HomeRoomSectionMenu fieldState={homeRoomSectionIDsFieldState} onBack={goToMain} />
				</If>
				<If condition={view === "courseSection"}>
					<CourseSectionMenu fieldState={courseSectionIDsFieldState} onBack={goToMain} />
				</If>
				<If condition={view === "userGroup"}>
					<UserGroupMenu fieldState={userGroupIDsFieldState} onBack={goToMain} />
				</If>
				<If condition={view === "user"}>
					<UserMenu fieldState={userIDsFieldState} onBack={goToMain} />
				</If>
			</Conditional>

			<If condition={view === "main"}>
				{!!onClose && (
					<Modal.Footer style={{ position: "static" }}>
						<Row justify="space-between" align="center">
							<ControlledSharedUsersPreviewListModalController formValues={formState.formValues} variant="link">
								View All
							</ControlledSharedUsersPreviewListModalController>

							<Button variant="primary" onClick={onClose}>
								Done
							</Button>
						</Row>
					</Modal.Footer>
				)}
			</If>
		</Modal>
	);
}

type MainMenuProps = Pick<
	AdvancedSharingModalProps,
	| "isPublicFieldState"
	| "includeEmployeesFieldState"
	| "includeGuardiansFieldState"
	| "includeAllUsersOfPersonTypesFieldState"
	| "classYearCategoryIDsFieldState"
	| "classYearIDsFieldState"
	| "courseSectionIDsFieldState"
	| "homeRoomSectionIDsFieldState"
	| "userGroupIDsFieldState"
	| "userIDsFieldState"
> & {
	setView: (view: MenuView) => void;
};

function MainMenu({
	includeEmployeesFieldState,
	includeGuardiansFieldState,
	includeAllUsersOfPersonTypesFieldState,
	classYearCategoryIDsFieldState,
	classYearIDsFieldState,
	courseSectionIDsFieldState,
	homeRoomSectionIDsFieldState,
	userGroupIDsFieldState,
	userIDsFieldState,
	setView,
}: MainMenuProps) {
	const includeEmployees = includeEmployeesFieldState.value;
	const includeGuardians = includeGuardiansFieldState.value;
	const includeAllUsersOfPersonTypes = includeAllUsersOfPersonTypesFieldState.value;
	const classYearCategoryIDs = classYearCategoryIDsFieldState.value;
	const classYearIDs = classYearIDsFieldState.value;
	const courseSectionIDs = courseSectionIDsFieldState.value;
	const homeRoomSectionIDs = homeRoomSectionIDsFieldState.value;
	const userGroupIDs = userGroupIDsFieldState.value;
	const userIDs = userIDsFieldState.value;

	const { data: classYearCategoryData } = useClassYearCategoryNamesQuery({
		variables: { filters: { idIn: classYearCategoryIDs } },
		skip: classYearCategoryIDs.length === 0,
	});
	const { data: classYearData } = useClassYearNamesQuery({
		variables: { filters: { idIn: classYearIDs } },
		skip: classYearIDs.length === 0,
	});
	const { data: homeRoomSectionData } = useHomeRoomSectionNamesQuery({
		variables: { filters: { idIn: homeRoomSectionIDs } },
		skip: homeRoomSectionIDs.length === 0,
	});
	const { data: courseSectionData } = useCourseSectionNamesQuery({
		variables: { filters: { idIn: courseSectionIDs } },
		skip: courseSectionIDs.length === 0,
	});
	const { data: userGroupData } = useUserGroupNamesQuery({
		variables: { filters: { idIn: userGroupIDs } },
		skip: userGroupIDs.length === 0,
	});
	const { data: userData } = useUserNamesQuery({
		variables: { filters: { idIn: userIDs } },
		skip: userIDs.length === 0,
	});

	const clearClassYearCategoryIDs = classYearCategoryIDsFieldState.clearValue;
	const clearClassYearIDs = classYearIDsFieldState.clearValue;
	const clearCourseSectionIDs = courseSectionIDsFieldState.clearValue;
	const clearHomeRoomSectionIDs = homeRoomSectionIDsFieldState.clearValue;
	const setIncludeAllUsersOfPersonTypes = includeAllUsersOfPersonTypesFieldState.setValue;
	const onClickIncludeAllUsersOfPersonTypes = React.useCallback(() => {
		const nextValue = !includeAllUsersOfPersonTypes;
		setIncludeAllUsersOfPersonTypes(nextValue);
		if (nextValue) {
			clearClassYearCategoryIDs();
			clearClassYearIDs();
			clearCourseSectionIDs();
			clearHomeRoomSectionIDs();
		}
	}, [
		includeAllUsersOfPersonTypes,
		setIncludeAllUsersOfPersonTypes,
		clearClassYearCategoryIDs,
		clearClassYearIDs,
		clearCourseSectionIDs,
		clearHomeRoomSectionIDs,
	]);

	const onClickView = React.useCallback(
		(view: MenuView) => {
			setView(view);
		},
		[setView],
	);

	const { hasAdminPermission } = React.useContext(SharingMenuContext);
	React.useEffect(() => {
		if (hasAdminPermission || !includeGuardians) {
			return;
		}

		if (includeAllUsersOfPersonTypesFieldState.value) {
			includeAllUsersOfPersonTypesFieldState.setValue(false);
		}
		if (classYearCategoryIDsFieldState.value.length > 0) {
			classYearCategoryIDsFieldState.setValue([]);
		}
		if (classYearIDsFieldState.value.length > 0) {
			classYearIDsFieldState.setValue([]);
		}
	}, [
		hasAdminPermission,
		includeGuardians,
		includeAllUsersOfPersonTypesFieldState,
		classYearCategoryIDsFieldState,
		classYearIDsFieldState,
	]);

	const areHighLevelButtonsDisabled = includeGuardians && !hasAdminPermission;
	const areSubMenusDisabled = !(includeGuardians || includeEmployees);

	return (
		<React.Fragment>
			<Modal.Header>
				<Heading.H2 noMargin>Share With</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<Grid tracks={2} gap="0.75rem">
						<Button
							onClick={() => includeGuardiansFieldState.setValue((prev) => !prev)}
							className={makeClassName(
								styles["user-category-button"],
								"button--tile",
								includeGuardians ? "button--tile--selected" : "",
							)}
						>
							<Column justify="spaced-start" align="center">
								<IconContainer className={styles["user-category-button__icon-container"]}>
									<Icon.AddressBook />
								</IconContainer>
								Guardians
							</Column>
						</Button>
						<Button
							onClick={() => includeEmployeesFieldState.setValue((prev) => !prev)}
							className={makeClassName(
								styles["user-category-button"],
								"button--tile",
								includeEmployees ? "button--tile--selected" : "",
							)}
						>
							<Column justify="spaced-start" align="center">
								<IconContainer className={styles["user-category-button__icon-container"]}>
									<Icon.IdentificationCard />
								</IconContainer>
								Staff
							</Column>
						</Button>
					</Grid>

					<Column justify="spaced-start" className={styles["person-type-filters-section"]}>
						<If condition={areSubMenusDisabled}>
							<div className={styles["person-type-filters-section__overlay"]}></div>
						</If>

						<Button
							onClick={onClickIncludeAllUsersOfPersonTypes}
							disabled={areSubMenusDisabled || areHighLevelButtonsDisabled}
							className={makeClassName(
								styles["main-button"],
								"button--tile",
								includeAllUsersOfPersonTypes ? "button--tile--selected" : "",
							)}
						>
							<Conditional>
								<If condition={includeEmployees && includeGuardians}>All Guardians and Staff</If>
								<If condition={includeEmployees}>All Staff</If>
								<If condition={includeGuardians}>All Guardians</If>
								<Else>Everyone</Else>
							</Conditional>
							<If condition={areHighLevelButtonsDisabled}>
								<TruncatedText className={styles["main-button__selected-preview"]}>
									You do not have permission to send to all guardians.
								</TruncatedText>
							</If>
						</Button>

						<strong>
							<Conditional>
								<If condition={includeEmployees && includeGuardians}>
									Guardians with Students in and Teachers and Administrators of...
								</If>
								<If condition={includeEmployees}>Teachers and Administrators of...</If>
								<If condition={includeGuardians}>Guardians with Students in...</If>
								<Else>With...</Else>
							</Conditional>
						</strong>
						<SubMenuButton
							label="Schools"
							description={
								areHighLevelButtonsDisabled
									? "You do not have permission to send to Guardians in this way."
									: englishCommaJoinStrings(
											classYearCategoryData?.classYearCategoryConnection.edges.map((e) =>
												ClassYearCategoryFormat.name(e.node),
											) ?? [],
									  )
							}
							isSelected={classYearCategoryIDs.length > 0}
							onClick={() => onClickView("classYearCategory")}
							disabled={areSubMenusDisabled || areHighLevelButtonsDisabled}
						/>
						<SubMenuButton
							label="Class Years"
							description={
								areHighLevelButtonsDisabled
									? "You do not have permission to send to Guardians in this way."
									: englishCommaJoinStrings(
											classYearData?.classYearConnection.edges.map((e) => ClassYearFormat.name(e.node)) ?? [],
									  )
							}
							isSelected={classYearIDs.length > 0}
							onClick={() => onClickView("classYear")}
							disabled={areSubMenusDisabled || areHighLevelButtonsDisabled}
						/>
						<SubMenuButton
							label="Home Rooms"
							description={englishCommaJoinStrings(
								homeRoomSectionData?.homeRoomSectionConnection.edges.map((e) => HomeRoomSectionFormat.name(e.node)) ??
									[],
							)}
							isSelected={homeRoomSectionIDs.length > 0}
							onClick={() => onClickView("homeRoomSection")}
							disabled={areSubMenusDisabled}
						/>
						<SubMenuButton
							label="Courses"
							description={englishCommaJoinStrings(
								courseSectionData?.courseSectionConnection.edges.map((e) => CourseSectionFormat.name(e.node)) ?? [],
							)}
							isSelected={courseSectionIDs.length > 0}
							onClick={() => onClickView("courseSection")}
							disabled={areSubMenusDisabled}
						/>
					</Column>

					<strong>Additional</strong>
					<SubMenuButton
						label="Groups"
						description={englishCommaJoinStrings(
							userGroupData?.userGroupConnection.edges.map((e) => UserGroupFormat.name(e.node)) ?? [],
						)}
						isSelected={userGroupIDs.length > 0}
						onClick={() => onClickView("userGroup")}
					/>
					<SubMenuButton
						label="People"
						description={englishCommaJoinStrings(
							userData?.userConnection.edges.map((e) => UserFormat.name(e.node)) ?? [],
						)}
						isSelected={userIDs.length > 0}
						onClick={() => onClickView("user")}
					/>
				</Column>
			</Modal.Body>
		</React.Fragment>
	);
}

type SubMenuButtonProps = {
	label: string;
	description?: string;
	isSelected: boolean;
	onClick: () => void;
	disabled?: boolean;
};

function SubMenuButton({ label, description, isSelected, onClick, disabled }: SubMenuButtonProps) {
	return (
		<Button
			onClick={onClick}
			className={makeClassName(styles["main-button"], "button--tile", isSelected ? "button--tile--selected" : "")}
			disabled={disabled}
		>
			<Row justify="space-between" align="center">
				<Column style={{ minWidth: 0 }}>
					{label}
					<If condition={!!description}>
						<TruncatedText className={styles["main-button__selected-preview"]}>{description}</TruncatedText>
					</If>
				</Column>
				<Icon.ArrowRight size={16} />
			</Row>
		</Button>
	);
}

type ClassYearCategoryMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearCategoryIDs"]>;
	onBack: () => void;
};

function ClassYearCategoryMenu({ fieldState, onBack }: ClassYearCategoryMenuProps) {
	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput(searchFormState.formValues, searchFormValuesToFilterInput);
	const { loading, data, error } = useClassYearCategorySelectQuery({ variables: { filters } });
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "classYearCategoryConnection" });

	return (
		<ObjectTypeMenu
			heading="Schools"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.classYearCategoryConnection.edges.map((e) => (
				<ClassYearCategoryOption key={e.node.id} fieldState={fieldState} classYearCategory={e.node} />
			))}
			ids={fieldState.value}
			emptyView={<EmptyView IconComponent={Icon.Users} primaryText="Schools" secondaryText="No schools to show" />}
			onBack={onBack}
		/>
	);
}

type ClassYearMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	onBack: () => void;
};

function ClassYearMenu({ fieldState, onBack }: ClassYearMenuProps) {
	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput(searchFormState.formValues, searchFormValuesToFilterInput);
	const { loading, data, error } = useClassYearSelectQuery({ variables: { filters } });
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "classYearConnection" });

	return (
		<ObjectTypeMenu
			heading="Class Years"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.classYearConnection.edges.map((e) => (
				<ClassYearOption key={e.node.id} fieldState={fieldState} classYear={e.node} />
			))}
			ids={fieldState.value}
			emptyView={
				<EmptyView IconComponent={Icon.Users} primaryText="Class Years" secondaryText="No class years to show" />
			}
			onBack={onBack}
		/>
	);
}

const homeRoomSectionBasicPermissionFilters: HomeRoomSectionFilterInput = {
	teacherHomeRoomSectionEnrollments: [
		{
			teacher: [
				{
					person: [
						{
							user: [
								{
									isCurrentEQ: true,
								},
							],
						},
					],
				},
			],
		},
	],
};

type HomeRoomSectionMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	onBack: () => void;
};

function HomeRoomSectionMenu({ fieldState, onBack }: HomeRoomSectionMenuProps) {
	const { hasAdminPermission } = React.useContext(SharingMenuContext);

	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput<SearchFormValues, HomeRoomSectionFilterInput>(
		searchFormState.formValues,
		searchFormValuesToFilterInput,
	);
	const { loading, data, error } = useHomeRoomSectionSelectQuery({
		variables: {
			filters: hasAdminPermission ? filters : mergeFilterInputs(filters, homeRoomSectionBasicPermissionFilters),
		},
	});
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "homeRoomSectionConnection" });

	return (
		<ObjectTypeMenu
			heading="Home Rooms"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.homeRoomSectionConnection.edges.map((e) => (
				<HomeRoomSectionOption key={e.node.id} fieldState={fieldState} homeRoomSection={e.node} />
			))}
			ids={fieldState.value}
			emptyView={
				<EmptyView IconComponent={Icon.Users} primaryText="Home Rooms" secondaryText="No home rooms to show" />
			}
			onBack={onBack}
		/>
	);
}

const courseSectionBasicPermissionFilters: CourseSectionFilterInput = {
	teacherCourseSectionEnrollments: [
		{
			teacher: [
				{
					person: [
						{
							user: [
								{
									isCurrentEQ: true,
								},
							],
						},
					],
				},
			],
		},
	],
};

type CourseSectionMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	onBack: () => void;
};

function CourseSectionMenu({ fieldState, onBack }: CourseSectionMenuProps) {
	const { hasAdminPermission } = React.useContext(SharingMenuContext);

	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput<SearchFormValues, CourseSectionFilterInput>(
		searchFormState.formValues,
		searchFormValuesToFilterInput,
	);
	const { loading, data, error } = useCourseSectionDetailedSelectListQuery({
		variables: {
			filters: hasAdminPermission ? filters : mergeFilterInputs(filters, courseSectionBasicPermissionFilters),
		},
	});
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "courseSectionConnection" });

	return (
		<ObjectTypeMenu
			heading="Courses"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.courseSectionConnection.edges.map((e) => (
				<CourseSectionOption key={e.node.id} fieldState={fieldState} courseSection={e.node} />
			))}
			ids={fieldState.value}
			emptyView={<EmptyView IconComponent={Icon.Users} primaryText="Courses" secondaryText="No courses to show" />}
			onBack={onBack}
		/>
	);
}

const userGroupBasicPermissionFilters: UserGroupFilterInput = {
	members: [
		{
			isCurrentEQ: true,
		},
	],
};

type UserGroupMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["userGroupIDs"]>;
	onBack: () => void;
};

function UserGroupMenu({ fieldState, onBack }: UserGroupMenuProps) {
	const { hasAdminPermission } = React.useContext(SharingMenuContext);

	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput<SearchFormValues, UserGroupFilterInput>(
		searchFormState.formValues,
		searchFormValuesToFilterInput,
	);
	const { loading, data, error } = useUserGroupDetailedSelectListQuery({
		variables: { filters: hasAdminPermission ? filters : mergeFilterInputs(filters, userGroupBasicPermissionFilters) },
	});
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "userGroupConnection" });

	return (
		<ObjectTypeMenu
			heading="Groups"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.userGroupConnection.edges.map((e) => (
				<UserGroupOption key={e.node.id} fieldState={fieldState} userGroup={e.node} />
			))}
			ids={fieldState.value}
			emptyView={<EmptyView IconComponent={Icon.Users} primaryText="Groups" secondaryText="No groups to show" />}
			onBack={onBack}
		/>
	);
}

type UserMenuProps = {
	fieldState: FieldState<SharingMenuFormValues["userIDs"]>;
	onBack: () => void;
};

function UserMenu({ fieldState, onBack }: UserMenuProps) {
	const searchFormState = useSearchFormState();
	const filters = IndexForms.useFilterInput(searchFormState.formValues, searchFormValuesToFilterInput);
	const { loading, data, error } = useUserNamesAndImagesQuery({ variables: { filters } });
	const queryStatus = getListQueryStatus({ loading, data, error, resultKey: "userConnection" });

	return (
		<ObjectTypeMenu
			heading="People"
			searchFormState={searchFormState}
			queryStatus={queryStatus}
			results={data?.userConnection.edges.map((e) => (
				<UserOption key={e.node.id} fieldState={fieldState} user={e.node} />
			))}
			ids={fieldState.value}
			emptyView={<EmptyView IconComponent={Icon.User} primaryText="People" secondaryText="No people to show" />}
			onBack={onBack}
		/>
	);
}

const pageSize = 25;

type ObjectTypeMenuProps = {
	heading: string;
	searchFormState: FormState<SearchFormValues>;
	queryStatus: QueryStatus;
	results: JSX.Element[] | null | undefined;
	ids: NodeID[];
	emptyView: JSX.Element;
	onBack: () => void;
};

function ObjectTypeMenu({
	heading,
	searchFormState,
	queryStatus,
	results,
	ids,
	emptyView,
	onBack,
}: ObjectTypeMenuProps) {
	const [pageIndex, setPageIndex] = React.useState(pageSize);

	const showMore = React.useCallback(() => {
		setPageIndex((prev) => prev + pageSize);
	}, []);
	const hasMore = pageIndex < (results?.length ?? 0);

	const [initialIDs] = React.useState(ids);
	const [showConfirmation, setShowConfirmation] = React.useState(false);
	React.useEffect(() => {
		const areEqual = initialIDs.length === ids.length && initialIDs.every((id) => ids.includes(id));
		setShowConfirmation(!areEqual);
	}, [ids, initialIDs]);

	return (
		<React.Fragment>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<Button variant="link" onClick={onBack} className="button--icon">
						<Icon.CaretLeft size={18} className="block" />
					</Button>

					<Heading.H2 noMargin>{heading}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body style={{ display: "flex", flexFlow: "column" }}>
				<Column justify="spaced-start" style={{ flexGrow: 1 }}>
					<SearchField formState={searchFormState} name="search" />

					<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ flexGrow: 1 }}>
						<Conditional>
							<If condition={queryStatus === "results"}>
								{results?.slice(0, pageIndex)}
								<If condition={hasMore}>
									<ViewableObserver onEnterView={showMore} />
								</If>
							</If>
							<If condition={queryStatus === "no-results"}>{emptyView}</If>
							<If condition={queryStatus === "loading"}>Loading...</If>
							<Else>Something went wrong.</Else>
						</Conditional>
					</Column>
				</Column>
			</Modal.Body>
			<If condition={showConfirmation}>
				<Modal.Footer>
					<Row justify="flex-end">
						<Button variant="primary" onClick={onBack}>
							Confirm Selection
						</Button>
					</Row>
				</Modal.Footer>
			</If>
		</React.Fragment>
	);
}

function searchFormValuesToFilterInput(formValues: SearchFormValues) {
	return {
		search: formValues.search,
	};
}
